import type { ButtonHTMLAttributes, ReactNode } from "react";
import { css, cx } from "@strust/ui/css";
import { button, spinner, type ButtonVariant } from "@strust/ui/recipes";

const ICON_SLOT = css({ display: "inline-flex" });

export type ButtonVisual = NonNullable<ButtonVariant["visual"]>;
export type ButtonSize = NonNullable<ButtonVariant["size"]>;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary (cyan, glow), secondary (brushed steel), ghost, danger, dangerOutline, warnOutline (amber, e.g. Unlock), link. */
  readonly visual?: ButtonVisual;
  readonly size?: ButtonSize;
  /** While true the button is not actionable and announces the busy state. */
  readonly loading?: boolean;
  /** Decorative icon before the label. */
  readonly icon?: ReactNode;
}

/** Steel Zero Trust button; `loading` shows a spinner, sets `aria-busy` and swallows clicks. */
export function Button({
  visual = "secondary",
  size = "md",
  loading = false,
  icon,
  type = "button",
  className,
  disabled,
  children,
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={loading ? undefined : onClick}
      className={cx(button({ visual, size }), className)}
    >
      {loading ? (
        <span aria-hidden="true" className={spinner()} />
      ) : icon ? (
        <span aria-hidden="true" className={ICON_SLOT}>
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, "children" | "aria-label" | "icon"> {
  /** Accessible name; icon-only buttons always need one (comes from i18n in the app). */
  readonly label: string;
  readonly icon: ReactNode;
}

/** Square icon-only button (brushed steel by default) named by `label`, also shown as tooltip. */
export function IconButton({
  label,
  icon,
  className,
  size = "md",
  visual = "secondary",
  loading = false,
  type = "button",
  onClick,
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      aria-label={label}
      title={label}
      aria-busy={loading || undefined}
      onClick={loading ? undefined : onClick}
      className={cx(button({ visual, size, iconOnly: true }), className)}
    >
      {loading ? (
        <span aria-hidden="true" className={spinner()} />
      ) : (
        <span aria-hidden="true" className={ICON_SLOT}>
          {icon}
        </span>
      )}
    </button>
  );
}
